import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import type { BookingType } from '@/lib/data/services';

/**
 * Bookings.
 *
 * Everything goes through our API, never straight to Supabase: a booking moves
 * money, and the state machine lives in Postgres functions the browser has no
 * grant to execute. There is deliberately no client-side path to change a
 * booking's status — project.md §56.
 */

export type BookingStatus =
  | 'requested'
  | 'confirmed'
  | 'active'
  | 'completed'
  | 'cancelled'
  | 'rejected'
  | 'expired'
  | 'no_show';

export interface Booking {
  id: string;
  user_id: string;
  companion_id: string;
  booking_type: BookingType;
  category_name: string;
  price_coins: number;
  price_unit: 'per_minute' | 'per_hour';
  scheduled_start: string | null;
  duration_minutes: number | null;
  location_text: string | null;
  meeting_note: string | null;
  reserved_coins: number;
  settled_coins: number;
  refunded_coins: number;
  status: BookingStatus;
  status_reason: string | null;
  expires_at: string | null;
  requested_at: string;
  confirmed_at: string | null;
  ended_at: string | null;
  /** Which side of this booking the signed-in person is on. */
  role: 'user' | 'companion';
}

/** Statuses where something can still happen. Everything else is history. */
export const isLive = (s: BookingStatus) =>
  s === 'requested' || s === 'confirmed' || s === 'active';

export const STATUS_LABEL: Record<BookingStatus, string> = {
  requested: 'Waiting for a reply',
  confirmed: 'Confirmed',
  active: 'Happening now',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rejected: 'Declined',
  expired: 'Expired',
  no_show: 'No-show',
};

export const STATUS_TONE: Record<BookingStatus, 'neutral' | 'marigold' | 'teal'> = {
  requested: 'marigold',
  confirmed: 'teal',
  active: 'teal',
  completed: 'neutral',
  cancelled: 'neutral',
  rejected: 'neutral',
  expired: 'neutral',
  no_show: 'neutral',
};

export function useBookings() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch<{ bookings: Booking[] }>('/api/bookings');
      setBookings(res.bookings);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your bookings.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { bookings, loading, error, refresh };
}

export interface CreateBookingInput {
  serviceId: string;
  bookingType: BookingType;
  scheduledStart?: string;
  durationMinutes?: number;
  location?: string;
  note?: string;
}

export const createBooking = (input: CreateBookingInput) =>
  apiFetch<{ bookingId: string }>('/api/bookings', { method: 'POST', body: input });

export const acceptBooking = (id: string) =>
  apiFetch<{ booking: Booking }>(`/api/bookings/${id}/accept`, { method: 'POST' });

export const cancelBooking = (id: string, reason?: string, noShow?: boolean) =>
  apiFetch<{ booking: Booking }>(`/api/bookings/${id}/cancel`, {
    method: 'POST',
    body: { reason, noShow },
  });

export const completeBooking = (id: string) =>
  apiFetch<{ booking: Booking }>(`/api/bookings/${id}/complete`, { method: 'POST' });

export interface AvailabilitySlot {
  weekday: number;
  starts_at: string;
  ends_at: string;
}

export interface TakenWindow {
  scheduled_start: string;
  duration_minutes: number;
}

export function useAvailability(companionId: string | undefined) {
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [taken, setTaken] = useState<TakenWindow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companionId) {
      setLoading(false);
      return;
    }
    let active = true;

    void apiFetch<{ slots: AvailabilitySlot[]; taken: TakenWindow[] }>(
      `/api/companions/${companionId}/availability`,
    )
      .then((r) => {
        if (!active) return;
        setSlots(r.slots);
        setTaken(r.taken);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [companionId]);

  return { slots, taken, loading };
}

/**
 * Whether a proposed window collides with something already confirmed.
 *
 * Advisory only — the exclusion constraint in the database is the real guard.
 * This exists so someone is told before they fill in a form, not after.
 */
export function collides(
  startIso: string,
  minutes: number,
  taken: TakenWindow[],
): boolean {
  const start = new Date(startIso).getTime();
  const end = start + minutes * 60_000;

  return taken.some((t) => {
    const ts = new Date(t.scheduled_start).getTime();
    const te = ts + t.duration_minutes * 60_000;
    return start < te && ts < end;
  });
}

/** "Sat 14 Sep, 3:00 pm" — the format an Indian user reads fastest. */
export function formatWhen(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Has this request run out of time?
 *
 * The worker sweeps expired requests to `expired` once a minute (see
 * apps/worker/src/jobs/expire-bookings.ts), so most of the time the status
 * already says so. This covers the gap between the deadline passing and the
 * next sweep — without it the page keeps offering Accept for up to a minute
 * after the server has started refusing it.
 *
 * Checked in the browser against the browser's clock, which can be wrong. That
 * is acceptable here: the database decides, and this only ever hides a button.
 * A skewed clock means an Accept that is briefly missing, never one that
 * succeeds when it should not.
 */
export function isExpiredRequest(b: Pick<Booking, 'status' | 'expires_at'>): boolean {
  if (b.status !== 'requested' || !b.expires_at) return false;
  return new Date(b.expires_at).getTime() <= Date.now();
}

export function formatDuration(minutes: number | null): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}
